import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { listBatches, type Batch } from "@/api/client";
import { batchLabel } from "@/components/batchLabel";
import { labelStatus } from "@/components/format";
import { SearchBox } from "@/components/SearchBox";
import { ErrorBanner } from "@/components/ui";

/** Finds a batch by name; once picked, shows its name and a Change button. */
export function BatchPicker({
  value,
  onChange,
  label = "Batch",
  status,
  hint,
}: {
  value: Batch | null;
  onChange: (batch: Batch | null) => void;
  label?: string;
  status?: string;
  hint?: string;
}) {
  const [q, setQ] = useState("");
  const results = useQuery({
    queryKey: ["batch-picker", q, status ?? ""],
    // No text lists the newest batches, so an untitled one can still be picked.
    queryFn: () => listBatches({ ...(q ? { q } : {}), pageSize: 25, ...(status ? { status } : {}) }),
    enabled: !value,
  });
  const items = results.data?.items ?? [];

  return (
    <div role="group" aria-label={label} className="text-sm">
      <span className="font-medium text-zinc-700">{label}</span>
      {value ? (
        <div className="mt-1 flex items-center gap-3">
          <span className="font-medium text-zinc-950">{batchLabel(value)}</span>
          <button type="button" onClick={() => onChange(null)} className="text-xs font-medium underline">
            Change
          </button>
        </div>
      ) : (
        <div className="mt-1 space-y-2">
          <div className="flex">
            <SearchBox q={q} onSearch={setQ} ariaLabel="Search batches" placeholder="Search by batch name" />
          </div>
          {hint ? <p className="text-xs text-zinc-500">{hint}</p> : null}
          <ErrorBanner error={results.error} />
          {results.isSuccess && items.length === 0 ? <p className="text-xs text-zinc-600">No batches match.</p> : null}
          {items.length > 0 ? (
            <ul className="divide-y divide-zinc-100 rounded-md border border-zinc-200">
              {items.map((b) => (
                <li key={b.id}>
                  <button type="button" onClick={() => onChange(b)} className="w-full px-3 py-2 text-left hover:bg-zinc-50">
                    {batchLabel(b)} · {labelStatus(b.status)} · {b.total_count ?? 0} recipients
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
    </div>
  );
}
